import { ComponentType, useCallback, useMemo } from 'react';

import {
  AppWindowIcon,
  BugIcon,
  CalendarCheckIcon,
  CowIcon,
  DnaIcon,
  EyedropperIcon,
  FlaskIcon,
  GearIcon,
  HouseIcon,
  MicroscopeIcon,
  SyringeIcon,
  TestTubeIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import { useFeatureFlags } from '@/app/services/features';
import { CalfIcon } from '@/components/icons/CalfIcon';
import { useInClinicBasePath } from '@/pages/in-clinic/inClinicPath';
import {
  hasAnyPermission,
  hasPermission,
  MODULES,
  useCurrentUser,
} from '@/services/auth';

export type NavItem = {
  label?: string;
  icon?: ComponentType<{ className?: string }>;
  path?: string;
  divider?: boolean;
  children?: NavItem[];
  isPreferencesButton?: boolean;
  isSignOutButton?: boolean;
  module?: string;
  permissionsAny?: string[];
  featureFlag?: string;
};

export type NavLeafItem = {
  label: string;
  module: string;
};

export function useNavigationItems() {
  const { t } = useTranslation();
  const me = useAppSelector((state) => state.authentication.me);
  const features = useFeatureFlags();
  const inClinicPath = useInClinicBasePath();
  const { data: currentUser } = useCurrentUser();
  const itTogActive =
    currentUser?.companies.some(
      (company) => company.code === 'TOG' && company.isActive,
    ) ?? false;

  const hasReadPermission = useCallback(
    ({ module, featureFlag, permissionsAny }: NavItem) => {
      if (!module && !featureFlag && !permissionsAny?.length) return true;

      if (permissionsAny?.length) {
        return (
          hasAnyPermission(me, permissionsAny) &&
          (!featureFlag || features[featureFlag])
        );
      }

      if (
        module === MODULES.LEGACY_APPS ||
        module === MODULES.DATABASE ||
        module === MODULES.FERT_WORK_READ ||
        module === MODULES.OF_TRANSFER_READ
      ) {
        return hasPermission(me, module);
      }

      return (
        hasPermission(me, `${module}.READ`) &&
        (!featureFlag || features[featureFlag])
      );
    },
    [features, me],
  );

  const navItems = useMemo<NavItem[]>(() => {
    const newBulkDonorSchedule =
      features['Separate_Bulk_Donor_Scheduling_Bulk_Donor'];
    return [
      { path: '/', label: t('nav.home', 'Home'), icon: HouseIcon },
      // Direct links (alphabetical)
      {
        path: '/animal-management',
        label: t('nav.animalManagement', 'Animal Management'),
        icon: CowIcon,
        module: MODULES.ANIMAL_MANAGEMENT,
      },
      {
        path: '/aspiration',
        label: t('nav.aspiration', 'Aspiration'),
        icon: SyringeIcon,
        module: MODULES.ASPIRATION,
      },
      {
        path: '/calf-entry',
        label: t('nav.calfEntry', 'Calf Entry'),
        icon: CalfIcon,
        module: MODULES.CALF_ENTRY,
      },
      {
        path: '/embryo',
        label: t('nav.embryo', 'Embryo'),
        icon: DnaIcon,
        permissionsAny: [MODULES.EMBRYO_READ],
      },
      ...(features.SemenPull
        ? [
            {
              path: '/semen',
              label: t('nav.semen', 'Semen'),
              icon: TestTubeIcon,
              module: MODULES.SEMEN_PULL,
              featureFlag: 'SemenPull',
            },
          ]
        : []),
      {
        path: '/shipping',
        label: t('nav.shipping', 'Shipping'),
        icon: TruckIcon,
        module: MODULES.SHIPPING,
      },
      // Accordion groups
      {
        label: t('nav.implant', 'Implant'),
        icon: EyedropperIcon,
        children: [
          {
            path: inClinicPath,
            label: t('nav.inClinic', 'In Clinic'),
            permissionsAny: [MODULES.CHUTE_IMPLANT_READ],
            featureFlag: 'In_Clinic_Nav',
          },
        ].filter((item) => hasReadPermission(item)),
      },
      {
        label: t('nav.csr', 'CSR'),
        icon: CalendarCheckIcon,
        children: [
          {
            path: '/chute-scheduler',
            label: t('nav.chuteScheduler', 'Chute Scheduler'),
            module: MODULES.CHUTE_SCHEDULER,
          },
          {
            path: '/donor-scheduling',
            label: t('nav.Donor_Scheduling', 'Donor Scheduling'),
            module: MODULES.DONOR_SCHEDULING_PARTNERSHIP,
          },
          ...(newBulkDonorSchedule && itTogActive
            ? [
                {
                  path: '/bulk/donor',
                  label: t('nav.Bulk_Donors', 'Bulk Donors'),
                  module: MODULES.BULK_DONOR_SCHEDULING,
                },
                {
                  label: t(
                    'nav.Bulk_Donor_Scheduling',
                    'Bulk Donor Scheduling',
                  ),
                  module: MODULES.BULK_DONOR_SCHEDULING,
                  children: [
                    {
                      path: '/bulk/donor-schedule',
                      label: t('nav.general', 'General'),
                    },
                    {
                      path: '/bulk/donor-schedule/farbro',
                      label: 'Farbro',
                    },
                    {
                      path: '/bulk/donor-schedule/agllc',
                      label: 'AgLLC',
                    },
                  ],
                },
              ]
            : [
                {
                  path: newBulkDonorSchedule
                    ? '/bulk/donor-schedule'
                    : '/bulk-donor-scheduling',
                  label: t(
                    'nav.Bulk_Donor_Scheduling',
                    'Bulk Donor Scheduling',
                  ),
                  module: MODULES.BULK_DONOR_SCHEDULING,
                },
              ]),
          {
            path: '/fresh-shipment?isCsr=true',
            label: t('nav.freshShipment', 'Fresh Shipment'),
            module: MODULES.FRESH_SHIPMENT,
          },
          {
            path: '/day-7-transfer-csr',
            label: t('nav.csrDay7', 'Day 7 Transfer CSR'),
            module: MODULES.DAY7,
          },
        ].filter((item) => hasReadPermission(item)),
      },
      {
        label: t('nav.ivfLab', 'IVF Lab'),
        icon: MicroscopeIcon,
        children: [
          {
            path: '/fertilization',
            label: t('nav.fertilizations', 'Fertilizations'),
            module: MODULES.FERTILIZATION,
          },
          {
            path: '/fert-work',
            label: t('nav.fertWork', 'Fert Work'),
            module: MODULES.FERT_WORK_READ,
          },
          {
            path: '/stripping',
            label: t('nav.stripping', 'Stripping'),
            module: MODULES.STRIPPING,
          },
          {
            path: '/fresh-shipment',
            label: t('nav.freshShipment', 'Fresh Shipment'),
            module: MODULES.FRESH_SHIPMENT,
          },
          {
            path: '/freeze',
            label: t('nav.freeze', 'Freeze'),
            module: MODULES.FREEZE,
          },
          {
            path: '/on-farm-implant',
            label: t('nav.onFarmImplant', 'On-Farm Implant'),
            featureFlag: 'On_Farm_Implant',
          },
        ].filter((item) => hasReadPermission(item)),
      },
      {
        label: t('nav.pse', 'PSE'),
        icon: FlaskIcon,
        children: [
          {
            path: '/opu-search',
            label: t('nav.opuSearch', 'OPU Search'),
            module: MODULES.OPU_SEARCH,
          },
          {
            path: '/day-7-transfer-pse',
            label: t('nav.pseDay7', 'Day 7 Transfer PSE'),
            module: MODULES.DAY7,
          },
        ].filter((item) => hasReadPermission(item)),
      },
      ...(!features.SemenPull
        ? [
            {
              label: t('nav.semenLab', 'Semen Lab'),
              icon: TestTubeIcon,
              children: [
                {
                  path: '/semen-pull',
                  label: t('nav.semenPull', 'Semen Pull'),
                  module: MODULES.SEMEN_PULL,
                },
              ].filter((item) => hasReadPermission(item)),
            },
          ]
        : []),
      {
        label: t('nav.issues', 'Issues'),
        icon: BugIcon,
        module: MODULES.ISSUES,
        children: [
          {
            label: t('nav.myIssues', 'My Issues'),
            children: [
              {
                path: '/issues/my-issues',
                label: t('nav.myIssues', 'My Issues'),
              },
              {
                path: '/issues/my-issues/open',
                label: t('nav.myPendingIssues', 'My Pending Issues'),
              },
              {
                path: '/issues/my-issues/closed',
                label: t('nav.myClosedIssues', 'My Closed Issues'),
              },
            ],
          },
          {
            label: t('nav.allIssues', 'All Issues'),
            children: [
              {
                path: '/issues/all',
                label: t('nav.allIssues', 'All Issues'),
              },
              {
                path: '/issues/all/open',
                label: t('nav.allPendingIssues', 'All Pending Issues'),
              },
              {
                path: '/issues/all/closed',
                label: t('nav.allClosedIssues', 'All Closed Issues'),
              },
            ],
          },
          {
            path: '/issues/new',
            label: t('nav.newIssue', 'New Issue'),
          },
          {
            path: '/issues/settings',
            label: t('nav.settings', 'Settings'),
          },
        ],
      },
      { divider: true, module: MODULES.LEGACY_APPS },
      {
        path: '/legacy-apps',
        label: t('nav.legacyApps', 'Legacy Apps'),
        icon: AppWindowIcon,
        module: MODULES.LEGACY_APPS,
      },
      { divider: true },
      {
        label: t('nav.settings', 'Settings'),
        icon: GearIcon,
        children: [
          {
            path: '/audit',
            label: t('nav.auditLog', 'Audit Log'),
            module: MODULES.DATABASE,
          },
          {
            isPreferencesButton: true,
            label: t('nav.preferences', 'Preferences'),
          },
          {
            isSignOutButton: true,
            label: t('nav.signOut', 'Sign Out'),
          },
        ].filter((item) => hasReadPermission(item)),
      },
    ].filter((item) => hasReadPermission(item));
  }, [t, hasReadPermission, features, itTogActive, inClinicPath]);

  return navItems;
}

export function getNavigationLeafItems(items: NavItem[]): NavLeafItem[] {
  return items.flatMap((item) => {
    if (item.children?.length) return getNavigationLeafItems(item.children);
    if (!item.label || !item.module) return [];
    return [{ label: item.label, module: item.module }];
  });
}
